import React from 'react'
import { Button, Tooltip } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import IosShareIcon from '@mui/icons-material/IosShare'
import { DashboardSignOff, DashboardSignOffRevoked } from '../../types/gotypes'
import DisabledReason from '../system/DisabledReason'

interface SignOffShareButtonsProps {
  signOff?: DashboardSignOff
  signOffRevoked?: DashboardSignOffRevoked
  /** ComputeReviewState's dashboard-level `config.needs_review` — the same flag
   *  that drives the amber banner. With `sign_off` set it makes the STALE state
   *  (#1156 amendment): green means signed off AND current, nothing else. */
  needsReview?: boolean
  /** The existing owner / Manager-Realm-on-promoted gate — sharing permission,
   *  deliberately independent of sign-off state (#1156). */
  showShareUpdate: boolean
  /** Why this viewer cannot sign off (they cannot edit the dashboard in
   *  place, which the server requires): Sign off shows disabled with it. */
  signOffDisabledReason?: string
  onSignOff: () => void
  onShareUpdate: () => void
}

// The Sign off and Share Update toolbar buttons (#1156, amended). Sharing and
// sign-off are separate concerns: Share Update is always available (subject
// only to the permission gate), and the PAIR carries the sign-off state as one
// colour — grey (never signed off), warning orange (revoked by an edit, or
// STALE: signed off but the job moved underneath it, both matching the banner
// treatment), success green (signed off and current). Green only when current:
// a name must never stay attached to numbers its owner has not seen.
const SignOffShareButtons: React.FC<SignOffShareButtonsProps> = ({
  signOff,
  signOffRevoked,
  needsReview,
  showShareUpdate,
  signOffDisabledReason,
  onSignOff,
  onShareUpdate,
}) => {
  const state: 'signed' | 'stale' | 'revoked' | 'unsigned' = signOff
    ? (needsReview ? 'stale' : 'signed')
    : signOffRevoked
      ? 'revoked'
      : 'unsigned'
  const color = state === 'signed' ? 'success' : state === 'revoked' || state === 'stale' ? 'warning' : 'inherit'

  const signOffButton = (
    <Button
      startIcon={state === 'revoked' || state === 'stale' ? <CancelIcon /> : <CheckCircleIcon />}
      onClick={onSignOff}
      variant="outlined"
      color={color}
      size="small"
    >
      {state === 'signed' ? 'Signed off' : 'Sign off'}
    </Button>
  )

  return (
    <>
      {signOffDisabledReason ? (
        <DisabledReason reason={signOffDisabledReason}>{signOffButton}</DisabledReason>
      ) : (
        <Tooltip
          title={
            signOff
              ? 'Sign off again (data has changed since last sign-off)'
              : 'Sign off this dashboard — a human has read it and it is ready for distribution'
          }
        >
          {signOffButton}
        </Tooltip>
      )}
      {showShareUpdate && (
        <Tooltip title="Copy a ready-to-send message with this dashboard's link">
          <Button
            startIcon={<IosShareIcon />}
            onClick={onShareUpdate}
            variant="outlined"
            color={color}
            size="small"
          >
            Share Update
          </Button>
        </Tooltip>
      )}
    </>
  )
}

export default SignOffShareButtons
